/**
 * Highlight Suggestions Component
 *
 * Displays category-specific highlight suggestions with:
 * - Persistent helper text
 * - Clickable suggestion chips
 * - Progressive disclosure (collapse after 3 highlights, hide at 8)
 * - Template handling (populate input with cursor at __)
 * - Complete handling (add directly as chip)
 *
 * Epic: MVP 1.20.1-S - Highlight Suggestions System
 */

import { useState } from 'react';
import { useHighlightSuggestions } from '../../hooks/experiences/useHighlightSuggestions';

// Category-specific helper text (always visible)
const HELPER_TEXT = {
  37: "Examples: Trophy species, Guide expertise, Included equipment, Best timing",  // Fishing
  38: "Examples: Game species, Habitat access, Guide expertise, Success rates",      // Hunting
  39: "Examples: Rapids class, Equipment, Safety, Route details",                     // Water Adventures
  40: "Examples: Summit elevation, Views, Technical skills, Guide certification",     // Mountain & Climbing
  41: "Examples: Trail distance, Camping nights, Scenery, Gear provided",           // Hiking & Backpacking
  42: "Examples: Route distance, Bike type, Support, Skill level",                   // Cycling
  43: "Examples: Snow conditions, Equipment, Instruction, Terrain",                   // Winter Sports
  48: "Examples: Species viewing, Photo opportunities, Guide expertise, Ethics",      // Wildlife
  45: "Examples: Certification, Hours, Practice, Instructor credentials",             // Skills & Education
  46: "Examples: Height/distance, Safety, Equipment, Experience level",               // Extreme Sports
  47: "Examples: Cultural authenticity, Guide background, Site access, Respect",      // Cultural & Heritage
  44: "Examples: Practice type, Setting, Instructor, Experience level"                // Wellness & Therapy
};

export default function HighlightSuggestions({
  categoryL1Id,
  currentHighlights = [],
  onTemplateClick,
  onCompleteClick
}) {
  const { suggestions, loading, error } = useHighlightSuggestions(categoryL1Id);
  const [isExpanded, setIsExpanded] = useState(true);

  const highlightCount = currentHighlights.length;
  const helperText = HELPER_TEXT[categoryL1Id] || "Add highlights that make your experience unique";

  // Progressive disclosure logic
  // 0-2 highlights: Show all
  // 3-7 highlights: Collapsible
  // 8 highlights: Hidden (maximum reached)
  if (highlightCount >= 8) {
    return null; // Maximum highlights reached, hide suggestions
  }

  const shouldBeCollapsible = highlightCount >= 3;

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    if (suggestion.isTemplate) {
      // Template: populate input field with text, user must fill in __
      onTemplateClick(suggestion.text);
    } else {
      // Complete: add directly as chip
      onCompleteClick(suggestion.text);
    }
  };

  return (
    <div className="space-y-2">
      {/* Persistent Helper Text */}
      <p className="text-xs text-gray-600">
        <span className="font-medium">Highlight ideas:</span> {helperText}
      </p>

      {/* Loading State */}
      {loading && (
        <div className="text-xs text-gray-500 py-2">
          Loading suggestions...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-xs text-red-600 py-2">
          Unable to load suggestions
        </div>
      )}

      {/* Suggestion Chips */}
      {!loading && !error && suggestions.length > 0 && (
        <div className="space-y-2">
          {/* Collapse/Expand Toggle (only show if 3+ highlights) */}
          {shouldBeCollapsible && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              {isExpanded ? '▼ Hide suggestions' : '▶ Show suggestions'}
            </button>
          )}

          {/* Chips Container (show if expanded OR if less than 3 highlights) */}
          {(isExpanded || !shouldBeCollapsible) && (
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="px-3 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full hover:bg-blue-100 hover:border-blue-300 transition-colors"
                  title={suggestion.isTemplate ? 'Click to use as template' : 'Click to add'}
                >
                  {suggestion.text}
                  {suggestion.isTemplate && (
                    <span className="ml-1 text-blue-500">✎</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
